import { useInsertPerformanceEvaluationRevisionMutation } from "gql/api";
import type { Ref } from "vue";
import { ref } from "vue";
import { useConfirm } from "primevue/useconfirm";
import useForm from "./forms/useForm";
import type { PerfEval } from "./forms/formTypes";
import { mapKeys } from "@/utils/utils";
import { useLogger } from "./logger";

export function useWipePerfEvals(perfEvals: Ref<PerfEval[]>) {
  const { executeMutation: deletePerfEvalsAndPumpXrefs } =
    useInsertPerformanceEvaluationRevisionMutation();
  const confirm = useConfirm();
  const { info } = useLogger("WIPE PERF EVALS");

  async function deleteAllPerfEvalsPrompt(): Promise<void> {
    return new Promise((resolve, reject) => {
      // immediately resolve if no perfevals in scenario
      if (perfEvals.value.length === 0) {
        resolve();
        return;
      }

      const perfEvalsWithDeletedAtNow = perfEvals.value.map(perfEval => ({
        ...perfEval,
        journal_deleted_at: "now()"
      }));

      const perfEvalsMapped = ref(
        mapKeys(perfEvalsWithDeletedAtNow, item => item.id)
      );

      const perfEvalXrefPumpsMapped = mapKeys(
        perfEvalsWithDeletedAtNow.flatMap(perfEval => perfEval.r_xref_pumps),
        item => item.id
      );

      const perfEvalsForm = useForm(perfEvalsMapped);
      const perfEvalXrefPumpsForm = useForm(ref(perfEvalXrefPumpsMapped));

      confirm.require({
        message:
          "Existing performance evaluations will be deleted after save. Continue?",
        header: "Warning",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Confirm",
        rejectLabel: "Cancel",
        accept() {
          info("Deleting Scenario Perf Evals");
          deletePerfEvalsAndPumpXrefs({
            update_performance_evaluations: true,
            update_pumps: Object.keys(perfEvalXrefPumpsMapped).length > 0,
            performance_evaluations_revisions:
              perfEvalsForm.prepareAllAsArray(),
            performance_evaluations_xref_pumps_revisions:
              perfEvalXrefPumpsForm.prepareAllAsArray()
          });
          resolve();
        },
        reject() {
          info("Cancelled Save Operation");
          reject(new Error("rejected"));
        }
      });
    });
  }

  return { deleteAllPerfEvalsPrompt };
}
